import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function Testimonials() {
  const { lang } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const [paused, setPaused] = useState(false);

  const { data: testimonials } = useQuery({
    queryKey: ["testimonials-landing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .eq("status", "published")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  if (!testimonials || testimonials.length === 0) return null;

  // Double the items for seamless loop
  const items = [...testimonials, ...testimonials];

  return (
    <section className="py-10 lg:py-24 bg-secondary/40">
      <div className="container px-6">
        <div className="max-w-2xl mb-14">
          <motion.p
            className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("Testimonials")}
          </motion.p>
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-balance"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            {t("Trusted by Kuwait's")} <span className="text-gradient">{t("Leading Teams")}</span>
          </motion.h2>
        </div>
      </div>

      {/* Marquee with fade masks */}
      <div
        className="relative overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Left fade */}
        <div className="absolute inset-y-0 left-0 w-24 sm:w-40 bg-gradient-to-r from-secondary/40 to-transparent z-10 pointer-events-none" />
        {/* Right fade */}
        <div className="absolute inset-y-0 right-0 w-24 sm:w-40 bg-gradient-to-l from-secondary/40 to-transparent z-10 pointer-events-none" />

        <div
          className="flex gap-6 w-max"
          style={{
            animation: `marquee-scroll ${testimonials.length * 16}s linear infinite`,
            animationPlayState: paused ? "paused" : "running",
          }}
        >
          {items.map((item: any, i: number) => (
            <div
              key={`${item.id}-${i}`}
              className="relative flex-shrink-0 w-[400px] sm:w-[480px] p-8 rounded-2xl bg-card border border-border shadow-card flex flex-col justify-between"
            >
              <div>
                <Quote className="h-8 w-8 text-primary/15 absolute top-6 right-6" />
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  "{lang === "ar" && item.quote_ar ? item.quote_ar : item.quote}"
                </p>
              </div>
              <div className="flex items-center gap-4">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="font-semibold text-foreground">
                    {lang === "ar" && item.name_ar ? item.name_ar : item.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {lang === "ar" && item.role_ar ? item.role_ar : item.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
